import { gql } from '@apollo/client';
import { FormLabel, Stack, useToast } from '@chakra-ui/react';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
import { toAvailableControlToggleOptions } from '~components/displays/utils';
import { FormLabelPendingIndicator } from '~components/ui/FormLabelPendingIndicator';
import { ConfigControlLockState } from '~graphql/__generated__/types';
import { fromError } from '~utils/errors';
import {
  ConfigDisplayControlsToggle,
  ConfigToggleOptionsConfig,
} from '../ConfigDisplayControlsToggle';
import {
  ConfigDisplayKeyboardControlToggle_ConfigFragment,
  useConfigUpdateKeyboardControlMutation,
} from './__generated__/ConfigDisplayKeyboardControlToggle.graphql';

interface Props {
  config: ConfigDisplayKeyboardControlToggle_ConfigFragment;
  isDisabled?: boolean;
}
export function ConfigDisplayKeyboardControlToggle({ config, isDisabled = false }: Props) {
  const toast = useToast();
  const [updateKeyboardState, { loading }] = useConfigUpdateKeyboardControlMutation();
  const { t } = useTranslation();

  const keyboardControlOptions: Array<ConfigToggleOptionsConfig<ConfigControlLockState>> = [
    { value: ConfigControlLockState.Locked, label: t('lockAllInputs') },
    { value: ConfigControlLockState.VolumeOnly, label: t('allowVolumeChange') },
    { value: ConfigControlLockState.PowerOnly, label: t('allowPowerChange') },
    { value: ConfigControlLockState.Unlocked, label: t('unlockAllInputs') },
  ];
  const handleChange = async (controlState: ConfigControlLockState) => {
    const { id } = config;

    try {
      updateKeyboardState({
        variables: {
          input: {
            configurationId: id,
            controlState,
          },
        },
        refetchQueries: ['CustomerConfigDetailPage'],
        awaitRefetchQueries: true,
      });
    } catch (error) {
      toast({
        status: 'error',
        title: t('controlUpdateErr'),
        description: fromError(error, 'ConfigUpdateKeyboardControl'),
      });
    }
  };

  const currentControlState = config.keyboardControl?.reported;

  const controlOptions = toAvailableControlToggleOptions(
    keyboardControlOptions,
    config.keyboardControl?.supportedValues,
  );

  if (isNil(currentControlState)) {
    return null;
  }

  return (
    <Stack direction="column" spacing="3">
      <FormLabel>
        {t('lockDisplayButtons')}
        <FormLabelPendingIndicator isPending={loading} />
      </FormLabel>

      <ConfigDisplayControlsToggle
        name="keyboardControlState"
        value={currentControlState}
        handleChange={handleChange}
        isLoading={loading}
        options={controlOptions}
        isDisabled={isDisabled}
      />
    </Stack>
  );
}

ConfigDisplayKeyboardControlToggle.graphql = {
  fragments: {
    ConfigDisplayKeyboardControlToggle_config: gql`
      fragment ConfigDisplayKeyboardControlToggle_config on ConfigurationModel {
        id
        keyboardControl {
          reported
          supportedValues
        }
      }
    `,
  },
  mutations: {
    ConfigUpdateKeyboardControl: gql`
      mutation ConfigUpdateKeyboardControl($input: ConfigurationUpdateKeyboardControlInput!) {
        configurationUpdateKeyboardControl(input: $input) {
          id
          keyboardControl {
            reported
            supportedValues
          }
        }
      }
    `,
  },
};
